import type { ErrorResponse } from "@apollo/client/link/error";
import type { Breadcrumb, Event } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { GraphQLError } from "graphql/error";
import type { Kind } from "graphql/language";
import { Config } from "services/configuration/eatSmartConfig";
import { logToSentry } from "../../utils/logger";

// --- Event readability normalization -------------------------------------------------------------
// Most "issues" in this project are not distinct bugs: the same logical event fragments into hundreds
// of issues because (a) dynamic fridge UUIDs are baked into the message string, (b) Sentry's default
// grouping folds in the in-flight GraphQL operation, and (c) the captureConsole fallback re-captures
// our own logs with an "[APP] [LEVEL]" prefix. normalizeSentryEvent (wired as beforeSend) rewrites the
// title to a stable, id-free form and pins a fingerprint for the known noisy clusters, so each one
// collapses to a single issue. Per-fridge visibility is preserved via the `fridgeId` tag, not the title.

// A v1–v5 UUID (covers fridge ids and dish-type ids alike).
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/giu;
// The "[APP] [WARNING] " style prefix our logger adds on the console fallback path.
const APP_PREFIX_RE = /^\[APP\] \[(?:ERROR|WARNING|INFO|DEBUG)\]\s*/u;
// A short, non-UUID id that follows "fridge:" (e.g. the truncated "78215" / "6e29b" in modal logs).
const FRIDGE_TOKEN_RE = /(\bfridge:\s*)[0-9a-z]+/giu;
// An object accidentally string-concatenated into the message.
const OBJECT_OBJECT_RE = /\s*\[object Object\]\s*$/u;

const normalizeTitle = (raw: string): string =>
	raw
		.replace(APP_PREFIX_RE, "")
		.replace(UUID_RE, "<id>")
		.replace(FRIDGE_TOKEN_RE, "$1<id>")
		.replace(OBJECT_OBJECT_RE, "")
		.trim();

// Stable fingerprints for the recurring clusters, matched against the normalized title. First match
// wins, so list more specific patterns first. Anything not matched keeps Sentry's default grouping,
// so genuinely new errors still surface as their own issues.
const CLUSTER_RULES: ReadonlyArray<readonly [RegExp, string]> = [
	[/^Refetching prices:/u, "price-refetch"],
	[/^Response from price refetch/u, "price-refetch-response"],
	[/^Setting initial data after broken subscription/u, "subscription-recovery-set-data"],
	[/^Sending initial query after broken subscription/u, "subscription-recovery-send-query"],
	[/^Subscription restored/u, "subscription-restored"],
	[/^Door opened without order/u, "door-opened-without-order"],
	[/^Successfully fetched \d+ unknown dish types/u, "dish-types-fetched"],
	[/^Added \d+ valid dish types out of \d+ fetched/u, "dish-types-added"],
	[/^Fetching unknown dish types on the fly/u, "dish-types-fetching"],
	[/^Filtered out \d+ incomplete dish types/u, "dish-types-filtered"],
	[/^Error sending cloudwatch metric/u, "cloudwatch-metric-error"],
	[/HTML5 Audio pool exhausted/u, "audio-pool-exhausted"],
	[/^Starting up modal displayed/u, "modal-starting-up"],
	[/^Empty fridge modal displayed/u, "modal-empty-fridge"],
	[/^Error modal displayed/u, "modal-error"],
	[/^Restricted modal displayed/u, "modal-restricted"],
	[/^Out of service modal displayed/u, "modal-out-of-service"],
	[/^Remote refill modal displayed/u, "modal-remote-refill"],
	[/^QR code is null/u, "qr-null-refetch"],
	[/Missing QR code on fridge/u, "qr-missing"],
	[/^QR lifecycle: transaction end/u, "qr-lifecycle-transaction-end"],
	[/^QR unchanged after transaction/u, "qr-unchanged-refetch"],
	[/^Refill started on fridge/u, "refill-started"],
	[/^Refill finished on fridge/u, "refill-finished"],
	[/Could not ping Google DNS/u, "dns-offline"],
	[/Load failed/u, "network-load-failed"],
];

const clusterFingerprint = (title: string): string | undefined =>
	CLUSTER_RULES.find(([pattern]) => pattern.test(title))?.[1];

export const normalizeSentryEvent = <T extends Event>(event: T): T => {
	if (typeof event.message === "string") {
		event.message = normalizeTitle(event.message);
	}
	if (event.logentry && typeof event.logentry.message === "string") {
		event.logentry.message = normalizeTitle(event.logentry.message);
	}
	for (const value of event.exception?.values ?? []) {
		if (typeof value.value === "string") {
			value.value = normalizeTitle(value.value);
		}
	}

	const title = event.message ?? event.logentry?.message ?? event.exception?.values?.[0]?.value ?? "";
	const fingerprint = clusterFingerprint(title);
	if (fingerprint) {
		event.fingerprint = [fingerprint];
	}

	return event;
};

export const initSentry = () => {
	if (window.MSW_ENABLED) {
		return;
	}

	Sentry.init({
		// when undefined, sentry does not send any events
		dsn: Config.tools.sentryDSN,
		environment: import.meta.env.MODE,
		normalizeDepth: 6,
		release: `terminal-app@${import.meta.env.APP_VERSION}+${import.meta.env.VITE_BUILD_NUMBER}`,
		transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
		beforeSend: normalizeSentryEvent,
		integrations: [
			Sentry.browserTracingIntegration(),
			// "warn" is deliberately excluded: it re-captured our own console-fallback logs (the
			// "[APP] [WARNING]" duplicates) and third-party noise (Howler's "HTML5 Audio pool exhausted").
			Sentry.captureConsoleIntegration({
				levels: ["error"],
			}),
		],
		initialScope: {
			tags: {
				fridgeId: localStorage.getItem("fridgeId"),
				tabletType: localStorage.getItem("tabletType"),
			},
			contexts: {
				fridgeInfo: {
					fridgeId: localStorage.getItem("fridgeId"),
					tabletType: localStorage.getItem("tabletType"),
				},
			},
		},
		// Performance Monitoring
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which
		// URLs distributed tracing should be enabled
		tracePropagationTargets: ["localhost"],
	});
};

type SentryErrorEnrichedData = {
	kind: string;
	query: Kind.DOCUMENT;
	variables: Record<string, string | number>;
};

// Collect data about fridge and GQL query for Sentry scope
const getErrorData = (error: ErrorResponse): SentryErrorEnrichedData => ({
	kind: error.operation.operationName,
	query: error.operation.query.kind,
	variables: error.operation.variables,
});

const getBreadcrumb = (graphQlError: GraphQLError): Breadcrumb => ({
	category: "query-path",
	message: graphQlError.path?.join(" > "),
});

// Apollo hands us values that are NOT Error instances: the AppSync realtime ws-drop arrives as a plain
// `{ errors: [{ message }] }` object, and graphQLErrors can be plain serialized objects too. Pull the
// human-readable message out of those shapes.
const extractErrorMessage = (value: unknown): string | undefined => {
	if (typeof value === "string") {
		return value;
	}
	if (value && typeof value === "object") {
		const maybe = value as { message?: unknown; errors?: unknown };
		if (typeof maybe.message === "string") {
			return maybe.message;
		}
		if (Array.isArray(maybe.errors) && maybe.errors.length > 0) {
			const first = maybe.errors[0] as { message?: unknown };
			if (typeof first?.message === "string") {
				return first.message;
			}
		}
	}
	return undefined;
};

// Sentry.captureException needs a real Error. A non-Error (plain object) makes Sentry synthesize the
// useless "Object captured as exception with keys: ..." title and collapse distinct errors into one
// issue. Coerce into a named Error with a meaningful message so the event is titled + grouped properly.
const toError = (value: unknown, name: string): Error => {
	if (value instanceof Error) {
		return value;
	}
	const error = new Error(extractErrorMessage(value) ?? "Unknown Apollo error");
	error.name = name;
	return error;
};

export const captureApolloError = (
	error: unknown,
	errorData: SentryErrorEnrichedData,
	extraData?: { [key: string]: unknown },
) => {
	Sentry.withScope((scope) => {
		if (errorData.kind) {
			scope.setTag("kind", errorData.kind);
		}
		// fridgeId is already a global tag from initSentry, but pin it from the failing operation's
		// variables too so it is guaranteed present and filterable on these events.
		if (errorData.variables?.id) {
			scope.setTag("fridgeId", String(errorData.variables.id));
		}
		if (errorData.query) {
			scope.setExtra("query", errorData.query);
		}
		if (errorData.variables) {
			scope.setExtra("variables", errorData.variables);
		}
		if (extraData) {
			for (const key of Object.keys(extraData)) {
				scope.setExtra(key, extraData[key]);
			}
		}
		if (error instanceof GraphQLError && error?.path) {
			scope.addBreadcrumb(getBreadcrumb(error));
		}
		const capturedError = toError(error, `${errorData.kind}Error`);
		// Keep the raw payload when we had to synthesize an Error so nothing is lost.
		if (capturedError !== error) {
			scope.setExtra("originalError", error);
		}
		Sentry.captureException(capturedError);
	});
};

export const handleSendApolloErrorToSentry = (error: ErrorResponse) => {
	const errorData = getErrorData(error);

	if (error.graphQLErrors) {
		for (const graphQlError of error.graphQLErrors) {
			// Pass the GraphQLError through untouched — spreading it into a plain object stripped the
			// Error prototype and produced the generic "Object captured..." title. captureApolloError
			// now coerces non-Errors itself.
			captureApolloError(graphQlError, errorData);
		}
		return;
	}

	if (error.networkError) {
		captureApolloError(error.networkError, errorData);
		return;
	}
	logToSentry("Application error", "error", [JSON.stringify(error)]);
};
